import React, { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL;

export default function FAQAdmin() {
  const [faqs, setFaqs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [form, setForm] = useState({ question: "", answer: "" });

  const fetchFaqs = async () => {
    try {
      const res = await axios.get(`${API}/faq/get-all-faqs`);
      setFaqs(res.data.data);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`${API}/faq/update-faq/${editingId}`, form);
      } else {
        await axios.post(`${API}/faq/create-faq`, form);
      }
      setForm({ question: "", answer: "" });
      setEditingId(null);
      setShowModal(false);
      fetchFaqs();
    } catch (err) {
      console.log(err);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (faq) => {
    setForm({ question: faq.question, answer: faq.answer });
    setEditingId(faq._id);
    setShowModal(true);
    setMobileMenuOpen(false);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/faq/delete-faq/${deleting}`);
      setDeleting(null);
      fetchFaqs();
    } catch (err) {
      console.log(err);
    }
  };

  const filtered = faqs.filter(
    (f) =>
      f.question.toLowerCase().includes(search.toLowerCase()) ||
      f.answer.toLowerCase().includes(search.toLowerCase())
  );

  const today = new Date().toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });

  return (
    <div className="min-h-screen bg-gray-50 font-sans">

      {/* Header */}
      <div className="bg-white px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between border-b border-gray-200 gap-4">
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 text-lg sm:text-xl flex-shrink-0">
            ❓
          </div>
          <div className="min-w-0 flex-1 sm:flex-none">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">FAQ Manager</h1>
            <p className="text-xs sm:text-sm text-gray-500 truncate">Manage all frequently asked questions from one place</p>
          </div>
        </div>
        <button
          onClick={() => {
            setForm({ question: "", answer: "" });
            setEditingId(null);
            setShowModal(true);
          }}
          className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl transition-colors text-sm w-full sm:w-auto"
        >
          <span className="text-lg leading-none">+</span>
          <span className="sm:inline">Add FAQ</span>
        </button>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6">

        {/* Search Bar */}
        <div className="bg-white rounded-xl border border-gray-200 px-3 sm:px-4 py-2.5 sm:py-3 flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
          <span className="text-gray-400 text-lg">🔍</span>
          <input
            type="text"
            placeholder="Search questions or answers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 outline-none text-sm text-gray-700 placeholder-gray-400 min-w-0"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="text-gray-400 hover:text-gray-600 text-sm transition-colors px-2 py-1"
            >
              ✕
            </button>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-[0.4fr_2fr_3fr_0.7fr] px-6 py-3 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            <span>#</span>
            <span>Question</span>
            <span>Answer</span>
            <span className="text-right">Actions</span>
          </div>

          {/* Rows */}
          {filtered.length === 0 ? (
            <div className="py-16 text-center text-gray-400">
              <div className="text-4xl mb-3">❓</div>
              <p className="font-medium text-gray-500">No FAQs found</p>
              <p className="text-sm mt-1">Try adjusting your search or add a new FAQ</p>
            </div>
          ) : (
            filtered.map((faq, i) => (
              <div
                key={faq._id}
                className={`grid grid-cols-[0.4fr_2fr_3fr_0.7fr] px-6 py-5 items-start ${
                  i < filtered.length - 1 ? "border-b border-gray-100" : ""
                } hover:bg-gray-50 transition-colors`}
              >
                {/* Index */}
                <div className="flex items-center pt-0.5">
                  <span className="w-7 h-7 rounded-full bg-blue-50 text-blue-600 text-xs font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                </div>

                {/* Question */}
                <div className="pr-6">
                  <p className="font-semibold text-gray-900 text-sm leading-relaxed">
                    {faq.question}
                  </p>
                </div>

                {/* Answer */}
                <div className="pr-6">
                  <p className="text-sm text-gray-500 leading-relaxed line-clamp-2">
                    {faq.answer}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3">
                  <button
                    onClick={() => handleEdit(faq)}
                    className="text-blue-400 hover:text-blue-600 transition-colors p-1"
                    title="Edit"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => setDeleting(faq._id)}
                    className="text-red-400 hover:text-red-600 transition-colors p-1"
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Mobile/Tablet Card View */}
        <div className="lg:hidden space-y-3 sm:space-y-4">
          {filtered.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 py-16 text-center text-gray-400">
              <div className="text-4xl mb-3">❓</div>
              <p className="font-medium text-gray-500">No FAQs found</p>
              <p className="text-sm mt-1">Try adjusting your search or add a new FAQ</p>
            </div>
          ) : (
            filtered.map((faq, i) => (
              <div
                key={faq._id}
                className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-50 text-blue-600 text-xs sm:text-sm font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base line-clamp-1">
                      {faq.question}
                    </h3>
                  </div>
                  <div className="flex gap-2 ml-2">
                    <button
                      onClick={() => handleEdit(faq)}
                      className="text-blue-400 hover:text-blue-600 transition-colors p-2 bg-blue-50 rounded-lg"
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => setDeleting(faq._id)}
                      className="text-red-400 hover:text-red-600 transition-colors p-2 bg-red-50 rounded-lg"
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                <div className="pl-11 sm:pl-13">
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>

                {/* Mobile metadata */}
                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
                  <span>FAQ #{i + 1}</span>
                  <span className="truncate max-w-[150px]">{faq.question.substring(0, 30)}...</span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="mt-4 sm:mt-6 px-4 sm:px-6 py-3 sm:py-4 bg-white rounded-xl border border-gray-200 flex flex-wrap items-center justify-between gap-3 text-xs sm:text-sm text-gray-500">
          <span>Total FAQs: <strong className="text-gray-800">{faqs.length}</strong></span>
          {search && (
            <span className="hidden sm:inline">Showing: <strong className="text-blue-600">{filtered.length}</strong> results</span>
          )}
          <span className="hidden md:inline">Last Updated: <strong className="text-gray-800">{today}</strong></span>
          
          {/* Mobile stats */}
          <div className="flex sm:hidden items-center gap-3 w-full justify-center border-t border-gray-100 pt-2 mt-1">
            {search && (
              <span>Results: <strong className="text-blue-600">{filtered.length}</strong></span>
            )}
            <span>Updated: <strong className="text-gray-800">{today}</strong></span>
          </div>
        </div>
      </div>

      {/* Add / Edit Modal - Responsive */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg shadow-2xl sm:mx-4 animate-slide-up sm:animate-none"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">
                {editingId ? "Edit FAQ" : "Add FAQ"}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none p-2"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="px-4 sm:px-6 py-4 sm:py-5 space-y-4 max-h-[70vh] overflow-y-auto">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                    Question *
                  </label>
                  <input
                    type="text"
                    name="question"
                    value={form.question}
                    onChange={handleChange}
                    placeholder="e.g. How do I reset my password?"
                    required
                    className="w-full border border-gray-200 rounded-lg px-3 py-3 sm:py-2.5 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                    Answer *
                  </label>
                  <textarea
                    name="answer"
                    value={form.answer}
                    onChange={handleChange}
                    placeholder="Write a clear and helpful answer..."
                    required
                    rows={5}
                    className="w-full border border-gray-200 rounded-lg px-3 py-3 sm:py-2.5 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition resize-none"
                  />
                </div>
              </div>

              <div className="px-4 sm:px-6 py-4 border-t border-gray-100 flex flex-col-reverse sm:flex-row gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-3 sm:py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition w-full sm:w-auto"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-3 sm:py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition w-full sm:w-auto"
                >
                  {saving ? "Saving..." : editingId ? "Save Changes" : "Create FAQ"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm - Responsive */}
      {deleting && (
        <div
          className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
          onClick={() => setDeleting(null)}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-sm shadow-2xl p-6 text-center animate-slide-up sm:animate-none"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center text-2xl mx-auto mb-4">
              🗑️
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete FAQ?</h3>
            <p className="text-sm text-gray-500 mb-6">
              This FAQ will be permanently removed. This cannot be undone.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setDeleting(null)}
                className="flex-1 px-4 py-3 sm:py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition order-2 sm:order-1"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-3 sm:py-2.5 text-sm font-semibold bg-red-500 hover:bg-red-600 text-white rounded-lg transition order-1 sm:order-2"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add custom animation styles */}
      <style jsx>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slideUp 0.3s ease-out;
        }
        
        @media (min-width: 640px) {
          .pl-13 {
            padding-left: 3.25rem;
          }
        }
      `}</style>
    </div>
  );
}